import { useI18n } from '@/hooks/useI18n';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Tile } from '@/components/primitives/Tile';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Skeleton } from '@/components/ui/skeleton';
import { useRetirementCandidates } from '@/hooks/queries/useRetirementCandidates';
import { formatNumber } from '@/lib/format';
import { SectionHeader } from './SectionHeader';
import { AnalyticsEmpty } from './AnalyticsEmpty';

export function RetirementCandidatesSection() {
  const { t } = useI18n();
  const { data, isLoading, isError, error, refetch } = useRetirementCandidates({
    minAgeDays: 30,
    dailyAvgThreshold: 100,
    periodDays: 14,
    limit: 20,
  });

  return (
    <section className="min-w-0 space-y-3">
      <SectionHeader
        title={t('Comptes à réévaluer', 'Accounts to review')}
        subtitle={t(
          '14 derniers jours · Comptes suivis depuis plus de 30 jours, avec moins de 100 vues gagnées par jour.',
          'Last 14 days · Accounts tracked for over 30 days with fewer than 100 views gained per day.',
        )}
      />
      <Tile padded>
        {isError ? (
          <ErrorInline error={error} onRetry={() => void refetch()} />
        ) : isLoading && !data ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded-[8px]" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <AnalyticsEmpty
            title={t('Aucun compte signalé', 'No accounts flagged')}
            description={t(
              'Aucun compte ne correspond aux critères avec les données disponibles.',
              'No accounts match these criteria in the available data.',
            )}
          />
        ) : (
          <div className="max-w-full overflow-x-auto rounded-[10px] border border-line-1">
            <table className="w-full min-w-[520px] text-[13px]">
              <thead className="bg-bg-2 text-left">
                <tr>
                  <th scope="col" className="px-3 py-2 font-semibold text-text-2">
                    {t('Compte', 'Account')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-text-2">
                    {t('Durée du suivi', 'Time tracked')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-text-2">
                    {t('Moy. vues / j', 'Avg. views / day')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-text-2">
                    {t('Priorité de revue', 'Review priority')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr
                    key={row.accountId}
                    className="border-t border-line-1 transition-colors hover:bg-bg-2"
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber" />
                        <Link
                          to={`/accounts/${row.accountId}`}
                          className="inline-flex min-h-10 items-center rounded font-medium text-text-1 underline-offset-4 hover:text-violet hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet"
                        >
                          @{row.username}
                        </Link>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-text-3">
                      {row.ageDays} {t('j', 'd')}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-text-3">
                      {formatNumber(Math.round(row.dailyAvgViews))}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose">
                      {Math.round(row.retireScore)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-[11px] leading-5 text-text-3">
          {t(
            'Priorité = jours de suivi ÷ max(vues moyennes par jour, 1). Vérifiez les relevés du compte avant toute décision : des données manquantes peuvent diminuer la moyenne.',
            'Priority = days tracked ÷ max(average daily views, 1). Check account measurements before deciding: missing data can lower the average.',
          )}
        </p>
      </Tile>
    </section>
  );
}
